import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Users, Share2, Info } from "lucide-react";
import { Project } from "@/types/Project";
import { useProjectMembers } from "@/hooks/useProjectMembers";
import { TeamMembersList } from "./TeamMembersList";
import { InviteMemberDialog } from "./InviteMemberDialog";
import { CreateMemberDialog } from "@/components/teams/CreateMemberDialog";
import { InvitationManagement } from "./InvitationManagement";
import { useAuth } from "@/hooks/use-auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ProjectTeamSectionProps {
  project: Project;
}

export function ProjectTeamSection({ project }: ProjectTeamSectionProps) {
  const { user } = useAuth();
  const {
    members,
    isLoading,
    inviteMember,
    updateMemberRole,
    removeMember,
    convertToTeamProject,
  } = useProjectMembers(project.id);

  const currentMember = members.find(m => m.user_id === user?.id);
  const currentUserRole = currentMember?.role || (project.owner_id === user?.id ? "owner" : "viewer");
  const canManageTeam = currentUserRole === "owner" || currentUserRole === "admin";

  if (!project.is_team_project) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Team Collaboration
          </CardTitle>
          <CardDescription>
            Convert this project to a team project to collaborate with others.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              This is currently a personal project. Converting it to a team project will allow you to:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Invite team members to collaborate</li>
                <li>Share time tracking responsibilities</li>
                <li>View individual and team analytics</li>
                <li>Maintain privacy for non-shared projects</li>
              </ul>
            </AlertDescription>
          </Alert>
          
          {project.owner_id === user?.id && (
            <Button onClick={() => convertToTeamProject()} className="w-full">
              <Share2 className="w-4 h-4 mr-2" />
              Convert to Team Project
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Team Members
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            Loading team members...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Team Management
        </CardTitle>
        <CardDescription>
          Manage team members and invitations for this project.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="members" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="invitations">Invitations</TabsTrigger>
          </TabsList>
          
          <TabsContent value="members" className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Current Members</h3>
              {canManageTeam && (
                <div className="flex gap-2">
                  <InviteMemberDialog onInvite={async (email, role) => {
                    await inviteMember({ email, role });
                  }} />
                  <CreateMemberDialog 
                    projectId={project.id} 
                    onClose={() => {}} 
                  />
                </div>
              )}
            </div>
            <TeamMembersList
              members={members}
              onUpdateRole={async (memberId, role) => {
                await updateMemberRole({ memberId, role });
              }}
              onRemoveMember={removeMember}
              currentUserRole={currentUserRole}
            />
          </TabsContent>
          
          <TabsContent value="invitations">
            <InvitationManagement 
              projectId={project.id}
              canManageInvitations={canManageTeam}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}